import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ProfileService,
  type KycDocStatusItem,
  type KycDocType,
  type KycHistoryItem,
} from '../services/profile.service';
import {
  REQUIRED_KYC_DOC_TYPES,
  filterHistoryByDocType,
  hasAllRequiredKycDocs,
} from '../utils/kycDocuments';

interface UseKycDocumentsOptions {
  enabled?: boolean;
  prefetchHistory?: boolean;
}

export const useKycDocuments = (options: UseKycDocumentsOptions = {}) => {
  const { enabled = true, prefetchHistory = true } = options;
  const [statuses, setStatuses] = useState<KycDocStatusItem[]>([]);
  const [history, setHistory] = useState<Record<KycDocType, KycHistoryItem[]>>({
    INE_FRONT: [],
    INE_BACK: [],
    ADDRESS_PROOF: [],
  });
  const [historyLoading, setHistoryLoading] = useState<Record<KycDocType, boolean>>({
    INE_FRONT: false,
    INE_BACK: false,
    ADDRESS_PROOF: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(
    async (docType: KycDocType) => {
      if (!enabled) return;
      setHistoryLoading((prev) => ({ ...prev, [docType]: true }));
      try {
        const res = await ProfileService.getKycHistory(docType);
        const items = Array.isArray(res?.items) ? res.items : [];
        setHistory((prev) => ({
          ...prev,
          [docType]: filterHistoryByDocType(items, docType),
        }));
      } catch (err: any) {
        setError(err?.message || 'No se pudo cargar historial KYC.');
      } finally {
        setHistoryLoading((prev) => ({ ...prev, [docType]: false }));
      }
    },
    [enabled],
  );

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ProfileService.getKycDocuments();
      const items = Array.isArray(res?.items) ? res.items : [];
      setStatuses(items);

      if (prefetchHistory) {
        await Promise.all(
          REQUIRED_KYC_DOC_TYPES.map((docType) => loadHistory(docType)),
        );
      }
    } catch (err: any) {
      setStatuses([]);
      setError(err?.message || 'No se pudo cargar documentos KYC.');
    } finally {
      setLoading(false);
    }
  }, [enabled, loadHistory, prefetchHistory]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const statusMap = useMemo(() => {
    const map = new Map<KycDocType, KycDocStatusItem>();
    statuses.forEach((item) => map.set(item.docType, item));
    return map;
  }, [statuses]);

  const hasRequiredDocs = useMemo(() => hasAllRequiredKycDocs(statuses), [statuses]);

  return {
    statuses,
    statusMap,
    history,
    historyLoading,
    hasRequiredDocs,
    loading,
    error,
    refresh,
    loadHistory,
  };
};
